import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/useEntity';
import { PageHeader } from '../../components/ui/PageHeader';
import { Badge } from '../../components/ui/Badge';
import { Select } from '../../components/ui/Select';
import { Input } from '../../components/ui/Input';
import { Modal } from '../../components/ui/Modal';
import { Button } from '../../components/ui/Button';
import { DataTable, type Column } from '../../components/DataTable';
import { fmt } from '../../lib/formatters';
import { downloadPedidoEml, type PedidoEmailItem } from '../../lib/pedidoMailto';
import { toast, confirmar } from '../../lib/toast';

type PedidoProveedorRow = {
  id: string;
  fecha: string;
  unidades: number;
  precio_compra_usd: number | null;
  estado: string;
  estado_nombre: string | null;
  estado_color: string | null;
  ref_externa: string | null;
  fecha_estimada_llegada: string | null;
  fecha_recepcion: string | null;
  notas: string | null;
  main_specs: string | null;
  incidencias: string | null;
  producto_id: string;
  sku: string;
  producto_nombre: string;
  proveedor_id: string | null;
  proveedor_nombre: string | null;
  ref_proveedor: string | null;
  subcategoria_id: string | null;
  subtipo_nombre: string | null;
  coste_cliente: number | null;
  margen_approx_pct: number | null;
  foc_proveedor_pct: number | null;
  cambio_usd_eur: number | null;
  precio_contenedor_eur: number | null;
  etd: string | null;
  email_generado_at: string | null;
  marca_nombre: string | null;
  cliente_codigo: string | null;
  cliente_nombre: string | null;
  pedido_cliente_id: string | null;
  pedido_cliente_numero_approx: string | null;
  numero_approx: string | null;
  pi_proveedor: string | null;
  numero_seq: number | null;
};

// Identificador interno legible del pedido a proveedor: PP-000045.
const ppCodigo = (n: number | null | undefined): string =>
  n == null ? '—' : `PP-${String(n).padStart(6, '0')}`;

// Adjunto de un pedido a proveedor (PDF de Nº pedido APPROX o de PI proveedor).
type PedidoArchivoRow = {
  id: string;
  tipo: string;
  nombre_original: string;
  mime_type: string;
  bytes: number;
  created_at: string;
  url: string | null;
  grupo_id: string;
  // Nº de pedidos que comparten este mismo documento (1 = no compartido).
  compartido_en: number;
};

const TEMPLATE_PEDIDO = 'Pedido a proveedor (centralización)';

type EstadoOption = { codigo: string; nombre: string; color: string };

// Campos editables de la ficha (como strings de formulario). Los derivados
// (foc/coste_cliente/cambio/transporte) se guardan como override en el pedido.
type FichaDraft = {
  proveedor_id: string;
  ref_proveedor: string;
  unidades: string;
  precio_compra_usd: string;
  foc: string;
  coste_cliente: string;
  cambio: string;
  transporte_40hq: string;
  etd: string;
  eta: string;
  estado: string;
  main_specs: string;
  notas: string;
  incidencias: string;
  numero_approx: string;
  pi_proveedor: string;
};

type ModRow = {
  id: string;
  fecha_hora: string;
  usuario_email: string | null;
  accion: string;
  cambios: Record<string, { antes?: unknown; despues?: unknown }>;
  observaciones: string | null;
};

// Etiqueta legible por campo de la tabla pedidos en el log.
const CAMPO_LABEL: Record<string, string> = {
  estado: 'Estado',
  unidades: 'Unidades',
  precio_compra_usd: 'Coste prov. (USD)',
  fecha_estimada_llegada: 'ETA',
  linea_etd: 'ETD',
  etd: 'ETD',
  main_specs: 'Main specs',
  notas: 'Notas',
  incidencias: 'Incidencias',
  foc_override: 'F.O.C.',
  cambio_override: 'Cambio EUR/USD',
  transporte_override: 'Transporte 40HQ',
  coste_cliente_override: 'Coste cliente',
  fecha_recepcion: 'Fecha recepción',
  ref_externa: 'Ref. externa',
  numero_approx: 'Pedido APPROX',
  pi_proveedor: 'PI proveedor',
  proveedor_producto: 'Proveedor (ficha del producto)',
  ref_proveedor_producto: 'Ref proveedor (ficha del producto)',
};

const HORA_FMT = new Intl.DateTimeFormat('es-ES', { hour: '2-digit', minute: '2-digit' });
const fmtHora = (v: string | null | undefined): string => {
  if (!v) return '—';
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? '—' : HORA_FMT.format(d);
};

const s = (v: unknown): string => (v === null || v === undefined ? '' : String(v));
function emptyDraft(): FichaDraft {
  return {
    proveedor_id: '', ref_proveedor: '',
    unidades: '', precio_compra_usd: '', foc: '', coste_cliente: '', cambio: '',
    transporte_40hq: '', etd: '', eta: '', estado: '', main_specs: '', notas: '', incidencias: '',
    numero_approx: '', pi_proveedor: '',
  };
}
function draftFromRow(r: PedidoProveedorRow): FichaDraft {
  return {
    proveedor_id: s(r.proveedor_id),
    ref_proveedor: s(r.ref_proveedor),
    unidades: s(r.unidades),
    precio_compra_usd: s(r.precio_compra_usd),
    foc: s(r.foc_proveedor_pct),
    coste_cliente: s(r.coste_cliente),
    cambio: s(r.cambio_usd_eur),
    transporte_40hq: s(r.precio_contenedor_eur),
    etd: s(r.etd),
    eta: s(r.fecha_estimada_llegada),
    estado: r.estado,
    main_specs: s(r.main_specs),
    notas: s(r.notas),
    incidencias: s(r.incidencias),
    numero_approx: s(r.numero_approx),
    pi_proveedor: s(r.pi_proveedor),
  };
}

export function PedidosProveedor() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [estado, setEstado] = useState('');
  const [proveedorId, setProveedorId] = useState('');
  const [clienteId, setClienteId] = useState('');
  const [marcaId, setMarcaId] = useState('');
  const [subcategoriaId, setSubcategoriaId] = useState('');
  const [fechaDesde, setFechaDesde] = useState('');
  const [fechaHasta, setFechaHasta] = useState('');
  // Ocultar pedidos en estado CANCELADO (marcado por defecto).
  const [ocultarCancelados, setOcultarCancelados] = useState(true);
  const [editing, setEditing] = useState<PedidoProveedorRow | null>(null);
  const [editMode, setEditMode] = useState(false);
  const [logOpen, setLogOpen] = useState(false);
  const [draft, setDraft] = useState<FichaDraft>(emptyDraft());
  // Snapshot del draft al entrar en edición, para enviar SOLO lo que cambió.
  const draftBase = useRef<FichaDraft>(emptyDraft());

  const abrirDetalle = (r: PedidoProveedorRow) => {
    setEditMode(false);
    setLogOpen(false);
    setEditing(r);
  };

  const [searchParams, setSearchParams] = useSearchParams();

  const logQuery = useQuery({
    queryKey: ['pedido-mods', editing?.id ?? ''],
    queryFn: () => api.get<{ data: ModRow[] }>(`/pedidos/${editing!.id}/modificaciones`),
    enabled: logOpen && !!editing,
  });
  const entrarEdicion = () => {
    if (!editing) return;
    const d = draftFromRow(editing);
    draftBase.current = d;
    setDraft(d);
    setEditMode(true);
  };
  const cerrarModal = () => { setEditMode(false); setLogOpen(false); setEditing(null); };

  const lista = useQuery({
    queryKey: ['pedidos-proveedor', 'listado-full', { estado, proveedorId, clienteId, marcaId, subcategoriaId, fechaDesde, fechaHasta }],
    queryFn: () => {
      const params = new URLSearchParams();
      if (estado) params.set('estado', estado);
      if (proveedorId) params.set('proveedor_id', proveedorId);
      if (clienteId) params.set('cliente_id', clienteId);
      if (marcaId) params.set('marca_id', marcaId);
      if (subcategoriaId) params.set('subcategoria_id', subcategoriaId);
      if (fechaDesde) params.set('fecha_desde', fechaDesde);
      if (fechaHasta) params.set('fecha_hasta', fechaHasta);
      const url = '/pedidos/listado/full' + (params.toString() ? `?${params}` : '');
      return api.get<{ data: PedidoProveedorRow[] }>(url);
    },
  });

  // Apertura automática del modal vía ?open=<id> (deep-link desde las líneas
  // de un pedido cliente). Se abre una sola vez y se limpia el parámetro.
  const openId = searchParams.get('open');
  const openedRef = useRef(false);
  useEffect(() => {
    if (!openId || openedRef.current) return;
    const row = lista.data?.data.find((r) => r.id === openId);
    if (row) {
      openedRef.current = true;
      abrirDetalle(row);
      searchParams.delete('open');
      setSearchParams(searchParams, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openId, lista.data]);

  const proveedores = useQuery({
    queryKey: ['list', '/proveedores'],
    queryFn: () => api.get<{ data: { id: string; nombre: string }[] }>('/proveedores'),
  });
  const clientes = useQuery({
    queryKey: ['list', '/admin/clientes'],
    queryFn: () => api.get<{ data: { id: string; nombre: string }[] }>('/admin/clientes'),
  });
  const marcas = useQuery({
    queryKey: ['list', '/admin/marcas'],
    queryFn: () => api.get<{ data: { id: string; nombre: string }[] }>('/admin/marcas'),
  });
  const subcategorias = useQuery({
    queryKey: ['list', '/admin/subcategorias'],
    queryFn: () => api.get<{ data: { id: string; nombre: string }[] }>('/admin/subcategorias'),
  });
  const estados = useQuery({
    queryKey: ['list', '/admin/pedido-estados'],
    queryFn: () => api.get<{ data: EstadoOption[] }>('/admin/pedido-estados'),
  });
  // Adjuntos del pedido en edición (PDF de Nº pedido APPROX y de PI proveedor).
  const archivos = useQuery({
    queryKey: ['pedido-proveedor-archivos', editing?.id],
    queryFn: () => api.get<{ data: PedidoArchivoRow[]; storage_configured: boolean }>(`/pedidos/${editing!.id}/archivos`),
    enabled: !!editing,
  });
  const archivoDe = (tipo: string): PedidoArchivoRow | null => archivos.data?.data.find((a) => a.tipo === tipo) ?? null;

  const cambiarEstado = useMutation({
    mutationFn: ({ id, estado }: { id: string; estado: string }) =>
      api.patch<{ sync_pedido_cliente: { estado_anterior: string; estado_nuevo: string } | null }>(
        `/pedidos/${id}/estado`, { estado },
      ),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['pedidos-proveedor'] });
      qc.invalidateQueries({ queryKey: ['pedidos-cliente'] });
      qc.invalidateQueries({ queryKey: ['pedido-cliente'] });
      if (data.sync_pedido_cliente) {
        const s = data.sync_pedido_cliente;
        // Notificación discreta — la UI ya invalidará y se verá actualizado.
        console.log(`[pedidos] pedido cliente actualizado de ${s.estado_anterior} → ${s.estado_nuevo}`);
      }
    },
  });

  // ── Selección múltiple + cambios masivos ────────────────────────────
  // Estado, ETD, ETA, Nº pedido APPROX y PI proveedor. Solo se envían los
  // campos que el usuario rellena.
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkEstado, setBulkEstado] = useState('');
  const [bulkEtd, setBulkEtd] = useState('');
  const [bulkEta, setBulkEta] = useState('');
  const [bulkApprox, setBulkApprox] = useState('');
  const [bulkPi, setBulkPi] = useState('');
  const resetBulkCampos = () => { setBulkEstado(''); setBulkEtd(''); setBulkEta(''); setBulkApprox(''); setBulkPi(''); };
  const bulkMut = useMutation({
    mutationFn: (updates: Record<string, unknown>) =>
      api.patch<{ updated: number; etd_aplicados: number; etd_omitidos: number }>('/pedidos/bulk', {
        ids: Array.from(selectedIds),
        updates,
      }),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['pedidos-proveedor'] });
      qc.invalidateQueries({ queryKey: ['pedidos-cliente'] });
      setSelectedIds(new Set());
      resetBulkCampos();
      if (data.etd_omitidos > 0) {
        toast.warning(`ETD aplicado a ${data.etd_aplicados} pedido(s)`, `${data.etd_omitidos} se omitieron por no estar vinculados a un pedido de cliente (el ETD se guarda en la línea del cliente).`);
      }
    },
  });
  const applyBulk = async () => {
    if (selectedIds.size === 0) return;
    const updates: Record<string, unknown> = {};
    if (bulkEstado) updates.estado = bulkEstado;
    if (bulkEtd) updates.etd = bulkEtd;
    if (bulkEta) updates.fecha_estimada_llegada = bulkEta;
    if (bulkApprox.trim()) updates.numero_approx = bulkApprox.trim();
    if (bulkPi.trim()) updates.pi_proveedor = bulkPi.trim();
    if (Object.keys(updates).length === 0) { toast.info('Rellena al menos un campo para aplicar el cambio masivo'); return; }
    const resumen = [
      bulkEstado ? `estado → "${estadoNombre(bulkEstado)}"` : null,
      bulkEtd ? `ETD → ${fmt.date(bulkEtd)}` : null,
      bulkEta ? `ETA → ${fmt.date(bulkEta)}` : null,
      bulkApprox.trim() ? `Pedido APPROX → "${bulkApprox.trim()}"` : null,
      bulkPi.trim() ? `PI proveedor → "${bulkPi.trim()}"` : null,
    ].filter(Boolean).join(', ');
    if (!(await confirmar({ title: `¿Aplicar a ${selectedIds.size} pedido(s)?`, message: `${resumen}.\n\nNota: el cambio masivo de estado NO propaga al pedido cliente; usa el click individual si necesitas sincronizar.`, confirmLabel: 'Aplicar', danger: true }))) return;
    bulkMut.mutate(updates);
  };
  const numSeleccionados = selectedIds.size;

  // ── Enviar email de pedido (centralización) ─────────────────────────
  const [emailLoading, setEmailLoading] = useState(false);
  const enviarEmail = async () => {
    if (selectedIds.size === 0) return;
    setEmailLoading(true);
    try {
      const ids = Array.from(selectedIds);
      const [datos, templates] = await Promise.all([
        api.post<{ data: PedidoEmailItem[] }>('/pedidos/email-data', { ids }),
        api.get<{ data: { nombre: string; destinatario_default: string | null; emails_cc_default: string[] | null }[] }>('/admin/email-templates'),
      ]);
      const tpl = (templates.data ?? []).find((t) => t.nombre === TEMPLATE_PEDIDO);
      if (!datos.data?.length) { toast.error('No se pudo generar el email del pedido', 'No se han encontrado datos de los pedidos seleccionados.'); return; }
      downloadPedidoEml(datos.data, { to: tpl?.destinatario_default ?? null, cc: tpl?.emails_cc_default ?? null });
      // email-data ya marcó email_generado_at en el servidor: refresca el tic.
      qc.invalidateQueries({ queryKey: ['pedidos-proveedor'] });
    } catch (err) {
      console.error('[pedidos] error generando email', err);
      toast.error('No se pudo generar el email del pedido', 'Revisa la consola.');
    } finally {
      setEmailLoading(false);
    }
  };

  // ── Guardar la ficha completa (edición + overrides + estado + audit) ──
  const guardarFicha = useMutation({
    mutationFn: ({ id, body }: { id: string; body: Record<string, unknown> }) =>
      api.patch(`/pedidos/${id}/ficha`, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['pedidos-proveedor'] });
      qc.invalidateQueries({ queryKey: ['pedidos-cliente'] });
      qc.invalidateQueries({ queryKey: ['pedido-cliente'] });
      cerrarModal();
    },
  });
  // Construye el body con SOLO los campos que cambiaron respecto al snapshot
  // inicial. Mapea los derivados a sus columnas override del pedido.
  const onGuardar = () => {
    if (!editing) return;
    const base = draftBase.current;
    const body: Record<string, unknown> = {};
    // Proveedor y ref se guardan en la ficha del PRODUCTO (el pedido los hereda).
    if (draft.proveedor_id !== base.proveedor_id) body.proveedor_id = draft.proveedor_id || null;
    if (draft.ref_proveedor !== base.ref_proveedor) body.ref_proveedor = draft.ref_proveedor.trim() || null;
    if (draft.unidades !== base.unidades) body.unidades = draft.unidades === '' ? 1 : Number(draft.unidades);
    if (draft.precio_compra_usd !== base.precio_compra_usd) body.precio_compra_usd = draft.precio_compra_usd === '' ? null : Number(draft.precio_compra_usd);
    if (draft.foc !== base.foc) body.foc_override = draft.foc === '' ? null : Number(draft.foc);
    if (draft.coste_cliente !== base.coste_cliente) body.coste_cliente_override = draft.coste_cliente === '' ? null : Number(draft.coste_cliente);
    if (draft.cambio !== base.cambio) body.cambio_override = draft.cambio === '' ? null : Number(draft.cambio);
    if (draft.transporte_40hq !== base.transporte_40hq) body.transporte_override = draft.transporte_40hq === '' ? null : Number(draft.transporte_40hq);
    if (draft.etd !== base.etd) body.etd = draft.etd || null;
    if (draft.eta !== base.eta) body.fecha_estimada_llegada = draft.eta || null;
    if (draft.estado !== base.estado) body.estado = draft.estado;
    if (draft.main_specs !== base.main_specs) body.main_specs = draft.main_specs;
    if (draft.notas !== base.notas) body.notas = draft.notas;
    if (draft.incidencias !== base.incidencias) body.incidencias = draft.incidencias;
    if (draft.numero_approx !== base.numero_approx) body.numero_approx = draft.numero_approx;
    if (draft.pi_proveedor !== base.pi_proveedor) body.pi_proveedor = draft.pi_proveedor;
    if (Object.keys(body).length === 0) { cerrarModal(); return; }
    guardarFicha.mutate({ id: editing.id, body });
  };

  // Traducción de código de estado → nombre y de un valor de cambio del log.
  const estadoNombre = (code: unknown): string => {
    const c = String(code ?? '');
    return (estados.data?.data ?? []).find((e) => e.codigo === c)?.nombre ?? (c || '—');
  };
  const renderVal = (campo: string, v: unknown): string =>
    campo === 'estado' ? estadoNombre(v) : (v == null || v === '' ? '—' : String(v));
  // Aplana las modificaciones en una fila por campo cambiado.
  const logRows = (logQuery.data?.data ?? []).flatMap((m) =>
    Object.entries(m.cambios ?? {})
      .filter(([, val]) => val && typeof val === 'object' && ('antes' in val || 'despues' in val))
      .map(([campo, val], i) => ({
        key: `${m.id}-${campo}-${i}`,
        fecha_hora: m.fecha_hora,
        campo: CAMPO_LABEL[campo] ?? campo,
        campoRaw: campo,
        antes: val.antes,
        despues: val.despues,
      })),
  );

  const filasTodas = lista.data?.data ?? [];
  const ccanceladosCount = filasTodas.filter((r) => r.estado === 'CANCELADO').length;
  const filas = ocultarCancelados ? filasTodas.filter((r) => r.estado !== 'CANCELADO') : filasTodas;
  const subtotal = filas.reduce((acc, r) => acc + (Number(r.precio_compra_usd) || 0) * r.unidades, 0);

  const cols: Column<PedidoProveedorRow>[] = [
    { key: 'numero_seq', label: 'ID interno', render: (r) => <span className="font-mono text-xs font-semibold text-slate-700">{ppCodigo(r.numero_seq)}</span>, sortValue: (r) => Number(r.numero_seq) },
    { key: 'fecha', label: 'Fecha', render: (r) => fmt.date(r.fecha), sortValue: (r) => r.fecha },
    { key: 'proveedor_nombre', label: 'Proveedor', sortValue: (r) => r.proveedor_nombre ?? '' },
    { key: 'ref_proveedor', label: 'Ref. proveedor', render: (r) => <span className="font-mono text-xs">{r.ref_proveedor ?? '—'}</span>, sortValue: (r) => r.ref_proveedor ?? '' },
    { key: 'unidades', label: 'Uds.', align: 'right', render: (r) => fmt.int(r.unidades), sortValue: (r) => r.unidades },
    { key: 'precio_compra_usd', label: 'Coste prov. (USD)', align: 'right', render: (r) => fmt.usd(r.precio_compra_usd), sortValue: (r) => Number(r.precio_compra_usd) },
    { key: 'cliente_codigo', label: 'Cliente', sortValue: (r) => r.cliente_codigo ?? '' },
    { key: 'marca_nombre', label: 'Marca', render: (r) => r.marca_nombre ?? '—', sortValue: (r) => r.marca_nombre ?? '' },
    { key: 'sku', label: 'SKU', render: (r) => <span className="font-mono text-xs">{r.sku}</span>, sortValue: (r) => r.sku },
    { key: 'producto_nombre', label: 'Descripción', sortValue: (r) => r.producto_nombre },
    {
      key: 'numero_approx',
      label: 'Pedido APPROX',
      align: 'center',
      render: (r) => r.numero_approx
        ? <span className="text-emerald-600" title={`Nº pedido APPROX: ${r.numero_approx}`}>✓</span>
        : <span className="text-slate-300">—</span>,
      sortValue: (r) => (r.numero_approx ? 1 : 0),
    },
    {
      key: 'pi_proveedor',
      label: 'PI Proveedor',
      align: 'center',
      render: (r) => r.pi_proveedor
        ? <span className="text-emerald-600" title={`PI proveedor: ${r.pi_proveedor}`}>✓</span>
        : <span className="text-slate-300">—</span>,
      sortValue: (r) => (r.pi_proveedor ? 1 : 0),
    },
    { key: 'estado', label: 'Estado', render: (r) => <Badge color={r.estado_color ?? '#64748b'}>{r.estado_nombre ?? r.estado}</Badge>, sortValue: (r) => r.estado_nombre ?? r.estado },
    {
      key: 'email_generado_at',
      label: 'Email',
      align: 'center',
      render: (r) => r.email_generado_at
        ? <span className="text-emerald-600" title={`Email generado ${fmt.datetime(r.email_generado_at)}`}>✓</span>
        : <span className="text-slate-300">—</span>,
      sortValue: (r) => (r.email_generado_at ? 1 : 0),
    },
    { key: 'etd', label: 'ETD', render: (r) => fmt.date(r.etd), sortValue: (r) => r.etd ?? '' },
    { key: 'fecha_estimada_llegada', label: 'ETA', render: (r) => fmt.date(r.fecha_estimada_llegada), sortValue: (r) => r.fecha_estimada_llegada ?? '' },
    {
      key: 'pedido_cliente_id',
      label: 'Pedido cliente',
      render: (r) => r.pedido_cliente_id
        ? (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); navigate(`/game/pedidos/${r.pedido_cliente_id}`); }}
            className="cursor-pointer text-xs font-mono text-sky-600 hover:underline"
          >{r.pedido_cliente_numero_approx ?? r.pedido_cliente_id.slice(0, 8)}</button>
        )
        : <span className="text-slate-300">—</span>,
      sortValue: (r) => r.pedido_cliente_numero_approx ?? '',
    },
    {
      key: 'acciones',
      label: '',
      align: 'right',
      render: (r) => r.estado === 'BORRADOR'
        ? (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              cambiarEstado.mutate({ id: r.id, estado: 'ENVIADO' });
            }}
            disabled={cambiarEstado.isPending}
            className="cursor-pointer rounded-md border border-emerald-300 bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-700 hover:bg-emerald-100 disabled:opacity-50"
          >
            Marcar como enviado
          </button>
        )
        : <span className="text-slate-300">—</span>,
    },
  ];

  return (
    <div>
      <PageHeader
        title="Pedidos a Proveedor"
        subtitle={`${filas.length} pedido(s) · ${fmt.int(filas.reduce((a, r) => a + r.unidades, 0))} ud · ${fmt.usd(subtotal)} total. Cambiar el estado aquí propaga automáticamente al pedido del cliente asociado.`}
      />

      <div className="mb-3 flex flex-wrap items-end gap-2">
        <FiltroCampo label="Estado pedido">
          <Select
            options={[{ value: '', label: 'Todos los estados' }, ...(estados.data?.data ?? []).map((e) => ({ value: e.codigo, label: e.nombre }))]}
            value={estado}
            emptyLabel={null}
            onChange={(e) => setEstado(e.target.value)}
            className="w-44 text-sm"
          />
        </FiltroCampo>
        <FiltroCampo label="Desde">
          <input
            type="date"
            value={fechaDesde}
            onChange={(e) => setFechaDesde(e.target.value)}
            className="h-9 rounded-md border border-slate-300 px-2 text-sm focus:border-sky-500 focus:outline-none focus:ring-1 focus:ring-sky-500"
          />
        </FiltroCampo>
        <FiltroCampo label="Hasta">
          <input
            type="date"
            value={fechaHasta}
            onChange={(e) => setFechaHasta(e.target.value)}
            className="h-9 rounded-md border border-slate-300 px-2 text-sm focus:border-sky-500 focus:outline-none focus:ring-1 focus:ring-sky-500"
          />
        </FiltroCampo>
        <FiltroCampo label="Proveedor">
          <Select
            options={[{ value: '', label: 'Todos' }, ...(proveedores.data?.data ?? []).map((p) => ({ value: p.id, label: p.nombre }))]}
            value={proveedorId}
            emptyLabel={null}
            onChange={(e) => setProveedorId(e.target.value)}
            className="w-48 text-sm"
          />
        </FiltroCampo>
        <FiltroCampo label="Cliente">
          <Select
            options={[{ value: '', label: 'Todos' }, ...(clientes.data?.data ?? []).map((c) => ({ value: c.id, label: c.nombre }))]}
            value={clienteId}
            emptyLabel={null}
            onChange={(e) => setClienteId(e.target.value)}
            className="w-40 text-sm"
          />
        </FiltroCampo>
        <FiltroCampo label="Marca">
          <Select
            options={[{ value: '', label: 'Todas' }, ...(marcas.data?.data ?? []).map((m) => ({ value: m.id, label: m.nombre }))]}
            value={marcaId}
            emptyLabel={null}
            onChange={(e) => setMarcaId(e.target.value)}
            className="w-44 text-sm"
          />
        </FiltroCampo>
        <FiltroCampo label="Subcategoría">
          <Select
            options={[{ value: '', label: 'Todas' }, ...(subcategorias.data?.data ?? []).map((s) => ({ value: s.id, label: s.nombre }))]}
            value={subcategoriaId}
            emptyLabel={null}
            onChange={(e) => setSubcategoriaId(e.target.value)}
            className="w-48 text-sm"
          />
        </FiltroCampo>
        {(estado || fechaDesde || fechaHasta || proveedorId || clienteId || marcaId || subcategoriaId) && (
          <button
            type="button"
            onClick={() => { setEstado(''); setFechaDesde(''); setFechaHasta(''); setProveedorId(''); setClienteId(''); setMarcaId(''); setSubcategoriaId(''); }}
            className="h-9 cursor-pointer text-xs text-sky-700 hover:text-sky-900 hover:underline"
          >
            Limpiar filtros
          </button>
        )}
        <label className="ml-auto flex h-9 cursor-pointer items-center gap-2 self-end text-sm text-slate-700">
          <input
            type="checkbox"
            checked={ocultarCancelados}
            onChange={(e) => setOcultarCancelados(e.target.checked)}
            className="h-4 w-4 cursor-pointer"
          />
          Ocultar cancelados{ccanceladosCount > 0 ? ` (${ccanceladosCount})` : ''}
        </label>
      </div>

      {numSeleccionados > 0 && (
        <div className="mb-3 space-y-3 rounded-md border border-sky-200 bg-sky-50 p-3 text-sm">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-semibold text-sky-900">
              {numSeleccionados} pedido{numSeleccionados === 1 ? '' : 's'} seleccionado{numSeleccionados === 1 ? '' : 's'}
            </span>
            <Button variant="primary" onClick={enviarEmail} disabled={emailLoading}>
              {emailLoading ? 'Generando…' : '✉️ Enviar email de pedido'}
            </Button>
            <div className="flex-1" />
            <button
              type="button"
              onClick={() => { setSelectedIds(new Set()); resetBulkCampos(); }}
              className="cursor-pointer text-xs text-sky-700 hover:text-sky-900 hover:underline"
            >
              Limpiar selección
            </button>
          </div>

          {/* ── Cambios masivos de campos ── */}
          <div className="flex flex-wrap items-end gap-3 border-t border-sky-200 pt-3">
            <div>
              <label className="block text-[10px] font-medium uppercase tracking-wide text-slate-500">Estado</label>
              <Select
                options={(estados.data?.data ?? []).map((e) => ({ value: e.codigo, label: e.nombre }))}
                value={bulkEstado}
                onChange={(e) => setBulkEstado(e.target.value)}
                emptyLabel="(sin cambio)"
                className="w-44"
              />
            </div>
            <div>
              <label className="block text-[10px] font-medium uppercase tracking-wide text-slate-500">ETD</label>
              <input type="date" value={bulkEtd} onChange={(e) => setBulkEtd(e.target.value)}
                className="h-9 rounded-md border border-slate-300 px-2 text-sm focus:border-sky-500 focus:outline-none focus:ring-1 focus:ring-sky-500" />
            </div>
            <div>
              <label className="block text-[10px] font-medium uppercase tracking-wide text-slate-500">ETA</label>
              <input type="date" value={bulkEta} onChange={(e) => setBulkEta(e.target.value)}
                className="h-9 rounded-md border border-slate-300 px-2 text-sm focus:border-sky-500 focus:outline-none focus:ring-1 focus:ring-sky-500" />
            </div>
            <div>
              <label className="block text-[10px] font-medium uppercase tracking-wide text-slate-500">Nº pedido APPROX</label>
              <input value={bulkApprox} onChange={(e) => setBulkApprox(e.target.value)} placeholder="(sin cambio)"
                className="h-9 w-44 rounded-md border border-slate-300 px-2 text-sm focus:border-sky-500 focus:outline-none focus:ring-1 focus:ring-sky-500" />
            </div>
            <div>
              <label className="block text-[10px] font-medium uppercase tracking-wide text-slate-500">PI proveedor</label>
              <input value={bulkPi} onChange={(e) => setBulkPi(e.target.value)} placeholder="(sin cambio)"
                className="h-9 w-44 rounded-md border border-slate-300 px-2 text-sm focus:border-sky-500 focus:outline-none focus:ring-1 focus:ring-sky-500" />
            </div>
            <Button variant="save" onClick={applyBulk} disabled={bulkMut.isPending}>
              {bulkMut.isPending ? 'Aplicando…' : 'Aplicar campos'}
            </Button>
          </div>

          {/* ── Adjuntar un mismo documento a todos los seleccionados ── */}
          <div className="border-t border-sky-200 pt-3">
            <p className="mb-2 text-[10px] font-semibold uppercase tracking-wide text-slate-500">
              Adjuntar documento a los {numSeleccionados} pedidos (se sube una sola vez y se comparte)
            </p>
            <div className="grid gap-3 sm:grid-cols-2">
              <BulkArchivoUploader ids={Array.from(selectedIds)} tipo="pedido_approx" label="PDF pedido APPROX" onDone={() => qc.invalidateQueries({ queryKey: ['pedido-proveedor-archivos'] })} />
              <BulkArchivoUploader ids={Array.from(selectedIds)} tipo="pi_proveedor" label="PDF PI proveedor" onDone={() => qc.invalidateQueries({ queryKey: ['pedido-proveedor-archivos'] })} />
            </div>
          </div>
        </div>
      )}

      <DataTable<PedidoProveedorRow>
        columns={cols}
        rows={filas}
        loading={lista.isLoading}
        rowKey={(r) => r.id}
        onRowClick={abrirDetalle}
        defaultSort={{ key: 'fecha', desc: true }}
        selectable
        selectedIds={selectedIds}
        onSelectionChange={setSelectedIds}
      />

      {editing && (
        <Modal
          open
          size="xl"
          onClose={cerrarModal}
          closeOnBackdrop={false}
          closeOnEsc={false}
          title={`${ppCodigo(editing.numero_seq)} · ${editing.sku} · ${editing.producto_nombre}${editMode ? '  ·  EDITANDO' : ''}`}
          footer={
            <div className="flex items-center gap-2">
              {editMode ? (
                <>
                  <Button variant="secondary" onClick={() => setEditMode(false)}>Cancelar</Button>
                  <Button variant="save" onClick={onGuardar} disabled={guardarFicha.isPending}>
                    {guardarFicha.isPending ? 'Guardando…' : 'Guardar'}
                  </Button>
                </>
              ) : (
                <>
                  <Button variant="secondary" onClick={cerrarModal}>Cerrar</Button>
                  <Button variant="secondary" onClick={() => setLogOpen(true)}>🕓 Log</Button>
                  <Button variant="primary" onClick={entrarEdicion}>✏️ Editar</Button>
                </>
              )}
            </div>
          }
        >
          {/* ── Producto de proveedor ── */}
          <dl className="grid grid-cols-1 gap-x-8 gap-y-2 text-sm sm:grid-cols-2">
            <Row k="Proveedor">
              {editMode
                ? (
                  <Select
                    options={(proveedores.data?.data ?? []).map((p) => ({ value: p.id, label: p.nombre }))}
                    value={draft.proveedor_id}
                    emptyLabel="(sin proveedor)"
                    onChange={(e) => setDraft((d) => ({ ...d, proveedor_id: e.target.value }))}
                  />
                )
                : (editing.proveedor_nombre ?? '—')}
            </Row>
            <Row k="Ref proveedor">
              {editMode
                ? <Input value={draft.ref_proveedor} onChange={(e) => setDraft((d) => ({ ...d, ref_proveedor: e.target.value }))} placeholder="Ref. del proveedor" />
                : <span className="font-mono">{editing.ref_proveedor ?? '—'}</span>}
            </Row>
            <Row k="Precio de coste (USD)">
              {editMode
                ? <NumInput value={draft.precio_compra_usd} onChange={(v) => setDraft((d) => ({ ...d, precio_compra_usd: v }))} />
                : fmt.usd(editing.precio_compra_usd)}
            </Row>
            <Row k="Unidades">
              {editMode
                ? <NumInput value={draft.unidades} onChange={(v) => setDraft((d) => ({ ...d, unidades: v }))} step="1" />
                : fmt.int(editing.unidades)}
            </Row>
            <Row k="F.O.C.">
              {editMode
                ? <NumInput value={draft.foc} onChange={(v) => setDraft((d) => ({ ...d, foc: v }))} suffix="%" />
                : fmt.pct(editing.foc_proveedor_pct)}
            </Row>
          </dl>

          <Divider />
          <div className="grid gap-4 sm:grid-cols-2">
            <CampoTexto label="Main specs" value={editMode ? draft.main_specs : (editing.main_specs ?? '')} editable={editMode} onChange={(v) => setDraft((d) => ({ ...d, main_specs: v }))} placeholder="Resumen técnico (se hereda del producto de proveedor; editable)" />
            <CampoTexto label="Notas del producto / pedido" value={editMode ? draft.notas : (editing.notas ?? '')} editable={editMode} onChange={(v) => setDraft((d) => ({ ...d, notas: v }))} placeholder="Notas del pedido (se incluyen en el email de centralización)" />
          </div>

          <Divider />
          {/* ── Datos cliente ── */}
          <dl className="grid grid-cols-1 gap-x-8 gap-y-2 text-sm sm:grid-cols-2">
            <Row k="Cliente">
              {editing.cliente_nombre ?? '—'} ({editing.cliente_codigo ?? '—'})
              {editing.pedido_cliente_id && (
                <button
                  type="button"
                  onClick={() => { cerrarModal(); navigate(`/game/pedidos/${editing.pedido_cliente_id}`); }}
                  className="ml-2 cursor-pointer text-xs font-mono text-sky-600 hover:underline"
                >{editing.pedido_cliente_numero_approx ?? editing.pedido_cliente_id.slice(0, 8)}</button>
              )}
            </Row>
            <Row k="Marca">{editing.marca_nombre ?? '—'}</Row>
            <Row k="Subtipo">{editing.subtipo_nombre ?? '—'}</Row>
            <Row k="SKU"><span className="font-mono">{editing.sku}</span></Row>
            <Row k="Coste cliente">
              {editMode
                ? <NumInput value={draft.coste_cliente} onChange={(v) => setDraft((d) => ({ ...d, coste_cliente: v }))} suffix="€" />
                : fmt.eur(editing.coste_cliente)}
            </Row>
            <Row k="Precios basados en cambio">
              {editMode
                ? <NumInput value={draft.cambio} onChange={(v) => setDraft((d) => ({ ...d, cambio: v }))} />
                : fmt.number(editing.cambio_usd_eur)}
            </Row>
            <Row k="Valor transporte 40HQ">
              {editMode
                ? <NumInput value={draft.transporte_40hq} onChange={(v) => setDraft((d) => ({ ...d, transporte_40hq: v }))} suffix="€" />
                : fmt.eur(editing.precio_contenedor_eur)}
            </Row>
          </dl>

          <Divider />
          {/* ── Referencias del pedido + adjuntos PDF (arrastrar y soltar) ── */}
          <div className="space-y-2">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-600">Referencias y documentos</p>
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1.5">
                <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">Nº pedido APPROX</div>
                {editMode
                  ? <Input value={draft.numero_approx} onChange={(e) => setDraft((d) => ({ ...d, numero_approx: e.target.value }))} placeholder="Ej. APX-00123" />
                  : <p className="text-sm text-slate-900">{editing.numero_approx || '—'}</p>}
                <PedidoArchivoSlot pedidoId={editing.id} tipo="pedido_approx" label="PDF pedido APPROX" archivo={archivoDe('pedido_approx')} onChanged={() => archivos.refetch()} />
              </div>
              <div className="space-y-1.5">
                <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">PI proveedor</div>
                {editMode
                  ? <Input value={draft.pi_proveedor} onChange={(e) => setDraft((d) => ({ ...d, pi_proveedor: e.target.value }))} placeholder="Ref. de la PI del proveedor" />
                  : <p className="text-sm text-slate-900">{editing.pi_proveedor || '—'}</p>}
                <PedidoArchivoSlot pedidoId={editing.id} tipo="pi_proveedor" label="PDF PI proveedor" archivo={archivoDe('pi_proveedor')} onChanged={() => archivos.refetch()} />
              </div>
            </div>
          </div>

          <Divider />
          <dl className="text-sm">
            <Row k="Margen Approx">
              <span className="font-semibold text-slate-900">{fmt.pct(editing.margen_approx_pct)}</span>
            </Row>
          </dl>

          <Divider />
          {/* ── Estado y fechas ── */}
          <dl className="grid grid-cols-1 gap-x-8 gap-y-2 text-sm sm:grid-cols-2">
            <Row k="Estado actual">
              {editMode
                ? (
                  <Select
                    options={(estados.data?.data ?? []).map((e) => ({ value: e.codigo, label: e.nombre }))}
                    value={draft.estado}
                    emptyLabel={null}
                    onChange={(e) => setDraft((d) => ({ ...d, estado: e.target.value }))}
                    className="w-56"
                  />
                )
                : <Badge color={editing.estado_color ?? '#64748b'}>{editing.estado_nombre ?? editing.estado}</Badge>}
            </Row>
            <Row k="ETD">
              {editMode
                ? <DateInput value={draft.etd} onChange={(v) => setDraft((d) => ({ ...d, etd: v }))} />
                : fmt.date(editing.etd)}
            </Row>
            <Row k="ETA">
              {editMode
                ? <DateInput value={draft.eta} onChange={(v) => setDraft((d) => ({ ...d, eta: v }))} />
                : fmt.date(editing.fecha_estimada_llegada)}
            </Row>
          </dl>
          {editMode && (
            <p className="mt-2 text-xs text-slate-500">
              Al guardar con estado EN_PRODUCCION se rellena el ETD de la línea (hoy + tiempo de producción) si está vacío; RECIBIDO_COMPLETO rellena la entrega real. El estado del pedido cliente se recalcula. Los cambios quedan en el log interno del pedido. Proveedor y su referencia se guardan en la ficha del producto (afectan a sus otros pedidos).
            </p>
          )}

          <Divider />
          <CampoTexto label="Incidencias surgidas durante el pedido" value={editMode ? draft.incidencias : (editing.incidencias ?? '')} editable={editMode} onChange={(v) => setDraft((d) => ({ ...d, incidencias: v }))} placeholder="Retrasos, defectos, cambios de cantidad, problemas de pago…" />
        </Modal>
      )}

      {editing && logOpen && (
        <Modal
          open
          onClose={() => setLogOpen(false)}
          size="xl"
          title={`Log de cambios — ${editing.sku}`}
          footer={<Button variant="secondary" onClick={() => setLogOpen(false)}>Cerrar</Button>}
        >
          {logQuery.isLoading
            ? <p className="text-sm text-slate-500">Cargando…</p>
            : logRows.length === 0
              ? <p className="text-sm text-slate-500">Sin cambios registrados todavía.</p>
              : (
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse text-sm">
                    <thead>
                      <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-500">
                        <th className="py-2 pr-3">Fecha</th>
                        <th className="py-2 pr-3">Hora</th>
                        <th className="py-2 pr-3">Campo modificado</th>
                        <th className="py-2 pr-3">Anterior</th>
                        <th className="py-2 pr-3">Nuevo</th>
                      </tr>
                    </thead>
                    <tbody>
                      {logRows.map((row) => (
                        <tr key={row.key} className="border-b border-slate-100">
                          <td className="py-1.5 pr-3 whitespace-nowrap">{fmt.date(row.fecha_hora)}</td>
                          <td className="py-1.5 pr-3 whitespace-nowrap text-slate-500">{fmtHora(row.fecha_hora)}</td>
                          <td className="py-1.5 pr-3 font-medium text-slate-700">{row.campo}</td>
                          <td className="py-1.5 pr-3 text-slate-500">{renderVal(row.campoRaw, row.antes)}</td>
                          <td className="py-1.5 pr-3 font-semibold text-slate-900">{renderVal(row.campoRaw, row.despues)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
        </Modal>
      )}
    </div>
  );
}

function Row({ k, children }: { k: string; children: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <dt className="text-slate-500">{k}</dt>
      <dd className="text-slate-900 text-right">{children}</dd>
    </div>
  );
}

function Divider() {
  return <hr className="my-4 border-slate-200" />;
}

const inputCls = 'w-44 rounded-md border border-slate-300 px-2 py-1 text-sm text-right focus:border-sky-500 focus:outline-none focus:ring-1 focus:ring-sky-500';

function NumInput({ value, onChange, step = 'any', suffix }: { value: string; onChange: (v: string) => void; step?: string; suffix?: string }) {
  return (
    <span className="inline-flex items-center gap-1">
      <input type="number" step={step} value={value} onChange={(e) => onChange(e.target.value)} className={inputCls} />
      {suffix && <span className="text-slate-400">{suffix}</span>}
    </span>
  );
}

function DateInput({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  return <input type="date" value={value} onChange={(e) => onChange(e.target.value)} className={inputCls} />;
}

function CampoTexto({ label, value, editable, onChange, placeholder }: { label: string; value: string; editable: boolean; onChange: (v: string) => void; placeholder?: string }) {
  return (
    <div>
      <label className="block text-xs font-semibold uppercase tracking-wide text-slate-600">{label}</label>
      {editable
        ? (
          <textarea
            value={value}
            onChange={(e) => onChange(e.target.value)}
            rows={3}
            placeholder={placeholder}
            className="mt-1 w-full rounded-md border border-slate-300 p-2 text-sm focus:border-sky-500 focus:outline-none focus:ring-1 focus:ring-sky-500"
          />
        )
        : <p className="mt-1 whitespace-pre-wrap text-sm text-slate-700">{value.trim() ? value : <span className="text-slate-300">—</span>}</p>}
    </div>
  );
}

function FiltroCampo({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-[10px] font-medium uppercase tracking-wide text-slate-500">{label}</label>
      {children}
    </div>
  );
}

// Slot de adjunto (PDF) de un pedido a proveedor: arrastrar y soltar o pulsar
// «Subir». Sustituye el anterior del mismo tipo (approx / pi). Funciona en
// vista y en edición (la subida es una acción independiente del Guardar).
function PedidoArchivoSlot({ pedidoId, tipo, label, archivo, onChanged }: {
  pedidoId: string;
  tipo: string;
  label: string;
  archivo: PedidoArchivoRow | null;
  onChanged: () => void;
}) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const subir = async (file: File) => {
    setBusy(true);
    setErr(null);
    try {
      const { supabase } = await import('../../lib/supabase');
      const { data: sess } = await supabase.auth.getSession();
      const token = sess.session?.access_token;
      const fd = new FormData();
      fd.append('file', file);
      fd.append('tipo', tipo);
      const res = await fetch(`/api/pedidos/${pedidoId}/archivos`, {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        body: fd,
      });
      if (!res.ok) throw new Error(`(${res.status}) ${await res.text()}`);
      onChanged();
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const borrar = async () => {
    if (!archivo) return;
    const otros = (archivo.compartido_en ?? 1) - 1;
    const msg = otros > 0
      ? `Este documento está asignado a otros ${otros} pedido${otros === 1 ? '' : 's'}. Al eliminarlo se quitará de todos ellos (${archivo.compartido_en} en total).`
      : undefined;
    if (!(await confirmar({ title: '¿Eliminar el archivo adjunto?', message: msg, confirmLabel: 'Eliminar', danger: true }))) return;
    setBusy(true);
    setErr(null);
    try {
      await api.delete(`/pedidos/${pedidoId}/archivos/${archivo.id}`);
      onChanged();
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      onDragOver={(e) => { e.preventDefault(); if (!dragOver) setDragOver(true); }}
      onDragLeave={(e) => { e.preventDefault(); setDragOver(false); }}
      onDrop={(e) => { e.preventDefault(); setDragOver(false); const f = e.dataTransfer.files?.[0]; if (f) subir(f); }}
      className={`flex flex-col items-center gap-1 rounded-lg border p-2 text-center transition ${dragOver ? 'border-sky-400 bg-sky-50 ring-2 ring-sky-300' : archivo ? 'border-slate-300 bg-white' : 'border-dashed border-slate-300 bg-slate-50'}`}
    >
      <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">{label}</p>
      <input
        ref={inputRef}
        type="file"
        className="hidden"
        onChange={(e) => { const f = e.target.files?.[0]; if (f) subir(f); if (inputRef.current) inputRef.current.value = ''; }}
      />
      {archivo ? (
        <>
          {archivo.url
            ? <a href={archivo.url} target="_blank" rel="noreferrer" className="line-clamp-2 cursor-pointer text-xs text-sky-600 hover:underline" title={archivo.nombre_original}>{archivo.nombre_original}</a>
            : <span className="line-clamp-2 text-xs text-slate-700" title={archivo.nombre_original}>{archivo.nombre_original}</span>}
          {(archivo.compartido_en ?? 1) > 1 && (
            <span className="rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-700" title="Documento compartido entre varios pedidos">
              Compartido · {archivo.compartido_en} pedidos
            </span>
          )}
          <div className="flex gap-2">
            <button type="button" onClick={() => inputRef.current?.click()} disabled={busy} className="cursor-pointer text-[11px] text-slate-500 hover:underline">{busy ? '…' : 'Sustituir'}</button>
            <button type="button" onClick={borrar} disabled={busy} className="cursor-pointer text-[11px] text-rose-600 hover:underline">Eliminar</button>
          </div>
        </>
      ) : (
        <>
          <p className="text-[11px] italic text-slate-400">{dragOver ? 'Suelta el PDF' : busy ? 'Subiendo…' : 'Arrastra el PDF aquí'}</p>
          <button type="button" onClick={() => inputRef.current?.click()} disabled={busy} className="cursor-pointer text-[11px] text-sky-600 hover:underline">+ Subir</button>
        </>
      )}
      {err && <p className="text-[10px] text-rose-600">{err}</p>}
    </div>
  );
}

// Sube UN archivo y lo asigna a todos los pedidos seleccionados a la vez. El
// blob se sube una sola vez y se comparte (un grupo_id). Si un pedido ya tenía
// un documento del mismo tipo, se sustituye en ese pedido.
function BulkArchivoUploader({ ids, tipo, label, onDone }: {
  ids: string[];
  tipo: string;
  label: string;
  onDone: () => void;
}) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);

  const subir = async (file: File) => {
    if (ids.length === 0) return;
    setBusy(true); setErr(null); setOk(null);
    try {
      const { supabase } = await import('../../lib/supabase');
      const { data: sess } = await supabase.auth.getSession();
      const token = sess.session?.access_token;
      const fd = new FormData();
      fd.append('file', file);
      fd.append('tipo', tipo);
      fd.append('ids', JSON.stringify(ids));
      const res = await fetch('/api/pedidos/archivos/bulk', {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        body: fd,
      });
      if (!res.ok) throw new Error(`(${res.status}) ${await res.text()}`);
      const data = await res.json() as { asignados: number };
      setOk(`Asignado a ${data.asignados} pedido${data.asignados === 1 ? '' : 's'}`);
      onDone();
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      onDragOver={(e) => { e.preventDefault(); if (!dragOver) setDragOver(true); }}
      onDragLeave={(e) => { e.preventDefault(); setDragOver(false); }}
      onDrop={(e) => { e.preventDefault(); setDragOver(false); const f = e.dataTransfer.files?.[0]; if (f) subir(f); }}
      className={`flex flex-col items-center gap-1 rounded-lg border p-2 text-center transition ${dragOver ? 'border-sky-400 bg-sky-50 ring-2 ring-sky-300' : 'border-dashed border-slate-300 bg-white'}`}
    >
      <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">{label}</p>
      <input
        ref={inputRef}
        type="file"
        className="hidden"
        onChange={(e) => { const f = e.target.files?.[0]; if (f) subir(f); if (inputRef.current) inputRef.current.value = ''; }}
      />
      <p className="text-[11px] italic text-slate-400">{dragOver ? 'Suelta el PDF' : busy ? 'Subiendo…' : 'Arrastra el PDF aquí'}</p>
      <button type="button" onClick={() => inputRef.current?.click()} disabled={busy} className="cursor-pointer text-[11px] text-sky-600 hover:underline">+ Subir y asignar</button>
      {ok && <p className="text-[10px] font-medium text-emerald-600">{ok}</p>}
      {err && <p className="text-[10px] text-rose-600">{err}</p>}
    </div>
  );
}
